import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Calculator, AlertTriangle, TrendingUp, ArrowRight, ChevronDown, Heart, Wallet, Users } from 'lucide-react';

const cards = [
  {
    id: 'life-change',
    icon: Heart,
    title: "My life changed and I'm not sure how it affects my taxes",
    content: "Got married? Bought a home? Had a child? Going through a divorce? These life events have major tax implications — from choosing between filing jointly or separately, to new deductions, credits, and withholding adjustments. Most people don't realize how much money is at stake until they've already overpaid. A quick review with the right CPA can make a real difference.",
    category: 'individual'
  },
  {
    id: 'taxes',
    icon: Calculator,
    title: "My taxes feel too high, but I don't know what I'm missing",
    content: "Whether you're filing as single, married filing jointly, or head of household — there's a big difference between a CPA who files your return and one who actively strategizes to reduce your tax burden. Deductions, credits, timing strategies, retirement contributions — most of these require planning throughout the year, not a scramble in April. If your CPA only talks to you during tax season, you're likely leaving money on the table.",
    category: 'both'
  },
  {
    id: 'multiple-income',
    icon: Wallet,
    title: "I have income from multiple sources and it's getting complicated",
    content: "W-2 salary plus a side gig. Investment income. Rental properties. Stock options or RSUs. When income comes from multiple sources, your tax situation gets complex fast — estimated payments, self-employment tax, capital gains timing, and passive income rules all come into play. DIY software wasn't built for this. A proactive CPA can help you stay organized and keep more of what you earn.",
    category: 'individual'
  },
  {
    id: 'structure',
    icon: Building2,
    title: "I'm not sure my business is structured the right way",
    content: "LLC, S-Corp, C-Corp — the difference between the right and wrong entity structure can mean tens of thousands of dollars in taxes every year. Most business owners set it up once and never revisit it, even as their revenue changes dramatically. We see this constantly, and it's one of the fastest wins we deliver for new clients.",
    category: 'business'
  },
  {
    id: 'irs',
    icon: AlertTriangle,
    title: "I have an IRS issue and I need help now",
    content: "Liens, levies, garnishments, unfiled returns — whether you're an individual or a business owner, these problems don't get better with time. They get worse. KGOB has a dedicated tax resolution team that handles IRS disputes, negotiates settlements, and represents you directly so you don't have to face the IRS alone. The sooner you act, the more options you have.",
    category: 'both'
  },
  {
    id: 'growth',
    icon: TrendingUp,
    title: "I'm growing fast and my books are a mess",
    content: "Growth is great until your financial infrastructure can't keep up. When you're cobbling together spreadsheets, guessing at cash flow, and dreading the monthly close — that's a sign you need a real accounting partner. We build clean, sustainable systems that grow with you, so you always know exactly where you stand.",
    category: 'business'
  }
];

export const PainPoints = () => {
  const [expanded, setExpanded] = useState(null);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="pain-points"
      className="relative bg-[#F7F6F2] py-20 lg:py-28"
      data-testid="welcome-pain-points"
    >
      <div className="absolute inset-0 bg-[radial-gradient(800px_400px_at_80%_20%,rgba(212,175,55,0.06),transparent)]" />

      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B2240]">
            Sound familiar?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#4C5A70]">
            Click the one that hits closest to home.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((card, i) => {
            const Icon = card.icon;
            const isOpen = expanded === card.id;
            return (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <button
                  onClick={() => setExpanded(isOpen ? null : card.id)}
                  className={`w-full text-left rounded-2xl p-6 border transition-all duration-300 ${
                    isOpen
                      ? 'bg-white border-[#D4AF37]/40 shadow-[0_8px_30px_rgba(11,34,64,0.1)]'
                      : 'bg-white/70 border-white/60 hover:bg-white hover:shadow-md hover:border-[#D4AF37]/20'
                  }`}
                  data-testid={`pain-card-${card.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      isOpen ? 'bg-[#0B2240]' : 'bg-[#0B2240]/10'
                    }`}>
                      <Icon className={`h-5 w-5 ${isOpen ? 'text-[#D4AF37]' : 'text-[#0B2240]'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-semibold text-[#0B2240] text-sm sm:text-base leading-snug">
                          {card.title}
                        </h3>
                        <ChevronDown className={`h-4 w-4 text-[#4C5A70] flex-shrink-0 transition-transform duration-200 ${
                          isOpen ? 'rotate-180' : ''
                        }`} />
                      </div>
                    </div>
                  </div>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <p className="mt-4 ml-14 text-sm text-[#4C5A70] leading-relaxed">
                          {card.content}
                        </p>
                        <div className="mt-4 ml-14">
                          <span
                            onClick={(e) => { e.stopPropagation(); scrollTo('contact'); }}
                            className="inline-flex items-center gap-1 text-sm font-medium text-[#D4AF37] hover:text-[#B99610] cursor-pointer transition-colors"
                            data-testid={`pain-cta-${card.id}`}
                          >
                            Want to talk about this? Let's connect
                            <ArrowRight className="h-4 w-4" />
                          </span>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
